import { useCallback, useEffect, useState } from "react";

// Third-parties.

// Helpers
import Helper from "../../../helpers/Helper";
import { fetchResults, formatDateForApi, getTimeZone } from "../../../helpers/utils";
import ActivityApi from "../../../api/instance/ActivityApi";
import { showError } from '../../../utils/showAlert';
import useChartOptions from "../../../helpers/useChartOptions";
import TimeSeriesChart, { TimeSeriesChartProps } from "../../../component/chart/echarts/TimeSeriesChart";

// Types
import { InstanceOptions, ActivityHashTimeSlice, ActivityHashData } from "./types";

// Context

//Constants
import { CHART_DATA_TYPE } from "../../../component/Constants";
import ConditionalRender from "../../../helpers/ConditionalRender";
import CombinedChanges from "../../../types/instance/CombinedChanges";

const SingleStatementChart = (props: {
    generateId: number,
    plansLoading: boolean,
    rowValues: any,
    combinedChanges: CombinedChanges[][],
    rowOriginal: any,
    interval: number,
    instanceOptions: InstanceOptions[]
}) => {
    const [loading, setLoading] = useState(true)
    const [chartData, setChartData] = useState<{
        [instanceIndex: string | number]: { [key: string]: { values: ActivityHashTimeSlice[], orderIndex: number } }
    }>({});

    const timezone = getTimeZone()
    const { rowValues } = props

    useEffect(() => {
        const start = async () => {
            try {
                setLoading(true)
                const promiseArray: any[] = []
                const dataToSet: any = {}
                props.instanceOptions.forEach((instanceOption: InstanceOptions) => {
                    promiseArray.push(getStatementResults(instanceOption))
                })

                const results = await Promise.all(promiseArray)

                results.forEach((result, index: number) => {
                    dataToSet[index] = result || []
                })

                setChartData(dataToSet)
                setLoading(false)
            } catch (error: any) {
                console.error('Failed to start single statement chart:', error);
                const errorMessage = error.message || error.toString();
                showError('Failed to load single statement chart data', errorMessage);
                setLoading(false);
                setChartData({});
            }
        }
        if (!props.plansLoading) {
            void start()
        }
    }, [props.combinedChanges, props.plansLoading])


    const getStatementResults = useCallback(async (instanceOption: InstanceOptions) => {
        try {
            let chartApiDataResults: ActivityHashData[][] = [[]]
            const sqlHash = rowValues.sqlhash
            const from = formatDateForApi(instanceOption.periodStart, timezone)
            const to = formatDateForApi(instanceOption.periodEnd, timezone)
            chartApiDataResults = await fetchResults([ActivityApi.fetchWaitEvents(from, to, timezone, instanceOption.instanceId, props.interval, `&&sort=waittime+desc&sqlhash=${sqlHash}`)])

            let chartTimePromises = []
            for (let sqlHashData of chartApiDataResults[0]) {
                const sqlHash = rowValues.sqlhash

                const encodedWaitEvent = sqlHashData.waitevent ? encodeURIComponent(sqlHashData.waitevent) : ''
                chartTimePromises.push(ActivityApi.fetchTime(from, to, timezone, instanceOption.instanceId, props.interval, `&id=${instanceOption.instanceId}&sqlhash=${sqlHash}&waitevent=${encodedWaitEvent}`))
            }

            const chartTimeResults = await fetchResults(chartTimePromises)

            // sort by time
            chartTimeResults.forEach(chartTime => {
                chartTime.sort(function compare(a: ActivityHashTimeSlice, b: ActivityHashTimeSlice) {
                    const aa: number = Helper.parseTimesliceWithTz(String(a.timeslice)).valueOf();
                    const bb: number = Helper.parseTimesliceWithTz(String(b.timeslice)).valueOf();
                    return aa < bb ? -1 : 1;
                })
            })

            const topNData: { [key: string]: { values: ActivityHashTimeSlice[], orderIndex: number } } = {}

            chartTimeResults.forEach((result: ActivityHashTimeSlice[], index: number) => {
                const sqlHashValue = chartApiDataResults[0][index].waitevent
                if (sqlHashValue) {
                    topNData[sqlHashValue] = {
                        values: result.map((item: any) => {
                            return {
                                timeslice: item.timeslice,
                                waittime: item.waittime,
                            }
                        }),
                        orderIndex: index + 1
                    }
                }

            })

            return topNData
        } catch (error: any) {
            console.error(error)
            const errorMessage = error.message || error.toString();
            showError('Failed to load single statement chart', errorMessage);
        }
    }, [chartData, props.combinedChanges])


    const chartOptions = useChartOptions(
            chartData,
            props.instanceOptions,
            {
                metric: 'waittime',
                tooltipType: CHART_DATA_TYPE.TIME,
                chart: 'column',
                type: 'waits',
                combinedChanges: props.combinedChanges.map(group => group.filter((c: any) => c.event !== 'plan')),
                interval: props.interval,
                height: 200,
            },
            props.generateId
    );

    return <div>
        <ConditionalRender if={loading || props.plansLoading}>
            <div className="w-100 text-center text-muted my-3 statement-chart">
                <div className="loader spinner">
                </div>
                <p className="mt-3">
                    Loading data...
                </p>
            </div>
        </ConditionalRender>

        <ConditionalRender if={!loading && !props.plansLoading}>
            <p>Top statement waits over time</p>

            <div id="collapseWaitsChart" className="card-body collapse show combined-charts mt-5">
                <ConditionalRender if={chartOptions[0]}>
                    <TimeSeriesChart {...(chartOptions[0] as TimeSeriesChartProps)}/>
                </ConditionalRender>
                <div className={"separator"}/>
                <ConditionalRender if={chartOptions[1]}>
                    <TimeSeriesChart {...(chartOptions[1] as TimeSeriesChartProps)}/>
                </ConditionalRender>
            </div>
        </ConditionalRender>
    </div>
}

export default SingleStatementChart;
